/**
 * SEC Filing Service
 * Handles API calls to the SEC Filing backend service
 */

import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';

const SEC_API_URL = config.api.secServiceUrl;

/**
 * Create axios instance with auth header
 */
const getAxiosInstance = () => {
  const token = localStorage.getItem('token');
  const instance = axios.create({
    baseURL: SEC_API_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Proactively refresh token before any request if within 30-minute expiry window
  applyTokenRefreshInterceptor(instance);
  return instance;
};

const secService = {
  /**
   * Health check
   */
  healthCheck: async () => {
    const api = getAxiosInstance();
    const response = await api.get('/sec/health');
    return response.data;
  },

  /**
   * Get all schedules
   */
  getAllSchedules: async () => {
    const api = getAxiosInstance();
    const response = await api.get('/sec/schedules');
    return response.data;
  },

  /**
   * Get specific schedule
   */
  getSchedule: async (scheduleId) => {
    const api = getAxiosInstance();
    const response = await api.get(`/sec/schedules/${scheduleId}`);
    return response.data;
  },

  /**
   * Create a new schedule
   */
  createSchedule: async (scheduleData) => {
    const api = getAxiosInstance();
    const response = await api.post('/sec/schedules', scheduleData);
    return response.data;
  },

  /**
   * Update a schedule
   */
  updateSchedule: async (scheduleId, updates) => {
    const api = getAxiosInstance();
    const response = await api.put(`/sec/schedules/${scheduleId}`, updates);
    return response.data;
  },

  /**
   * Delete a schedule
   */
  deleteSchedule: async (scheduleId) => {
    const api = getAxiosInstance();
    const response = await api.delete(`/sec/schedules/${scheduleId}`);
    return response.data;
  },

  /**
   * Manually trigger a poll for a schedule
   */
  triggerPoll: async (scheduleId) => {
    const api = getAxiosInstance();
    const response = await api.post(`/sec/schedules/${scheduleId}/poll`);
    return response.data;
  },

  /**
   * Backfill last 50 filings for a schedule
   */
  backfillSchedule: async (scheduleId) => {
    const api = getAxiosInstance();
    const response = await api.post(`/sec/schedules/${scheduleId}/backfill`);
    return response.data;
  },

  /**
   * Get recent filings
   */
  getRecentFilings: async (scheduleId = null, limit = 100) => {
    const api = getAxiosInstance();
    const params = { limit };
    if (scheduleId) {
      params.schedule_id = scheduleId;
    }
    const response = await api.get('/sec/filings', { params });
    return response.data;
  },

  // ==================== Burst Polling Session Methods ====================

  /**
   * Get all burst polling sessions
   */
  getAllBurstSessions: async () => {
    const api = getAxiosInstance();
    const response = await api.get('/sec/burst-sessions');
    return response.data;
  },

  /**
   * Get a specific burst session
   */
  getBurstSession: async (sessionId) => {
    const api = getAxiosInstance();
    const response = await api.get(`/sec/burst-sessions/${sessionId}`);
    return response.data;
  },

  /**
   * Create a new burst polling session
   * @param {Object} sessionData - { schedule_id, name, start_time, duration_minutes }
   */
  createBurstSession: async (sessionData) => {
    const api = getAxiosInstance();
    const response = await api.post('/sec/burst-sessions', sessionData);
    return response.data;
  },

  /**
   * Delete a burst polling session
   */
  deleteBurstSession: async (sessionId) => {
    const api = getAxiosInstance();
    const response = await api.delete(`/sec/burst-sessions/${sessionId}`);
    return response.data;
  },
};

export default secService;
